import { inject, Injectable } from "@angular/core";
import { LoadingState } from "@tsin-core/entities";
import { IStationResponseModel, StationRequestModel, StationStateModel } from "./station.model";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { StationService } from "@tsin-core/services/http/station.service";
import { AssignStation, CreateStation, DeleteStation, GetActivityHistory, ReplaceScheduleInterviewer, ReplaceStationInterviewer, Rotation, SetFilteredStations, SetStationSearch, Station, Stations, UpdateStation } from "./station.action";
import { catchError, tap } from "rxjs";

@State<StationStateModel>({
  name: 'stationState',
  defaults: {
    searchQuery: '',
    stations: [],
    station: null,
    rotations: [],
    activityHistory: [],
    loading: LoadingState.notLoading
  }
})

@Injectable()

export class StationState {

  private stationService = inject(StationService)

  @Selector()
  static getStations(state: StationStateModel) {
    return state.stations;
  }

  @Selector()
  static getRotations(state: StationStateModel) {
    return state.rotations;
  }

  @Selector()
  static getLoading(state: StationStateModel) {
    return state.loading;
  }

  @Selector()
  static filteredItems(state: StationStateModel) {
    // console.log('searchQuery: ', state.searchQuery)
    const query = state.searchQuery;
    if (typeof query === 'object' && 'cohortId' in query) {
      if (query?.cohortId !== undefined) {
        return state.stations.filter((station: any) =>
          station?.cohort?.id === query?.cohortId
        );
      } else {
        return state.stations;
      }
    } else if (typeof query === 'object' && 'administrationId' in query) {
      if (query?.administrationId !== undefined) {
        return state.stations.filter((station: any) =>
          station?.administration?.id === query?.administrationId
        );
      } else {
        return state.stations;
      }
    } else if (typeof query === 'object' && 'trackId' in query) {
      if (query?.trackId !== undefined) {
        return state.stations.filter((station: any) =>
          station?.track?.id === query?.trackId
        );
      } else {
        return state.stations;
      }
    } else if (typeof query === 'object' && 'cohortId' in query && 'administrationId' in query && 'trackId' in query) {
      if (query?.cohortId !== undefined && query?.administrationId !== undefined && query?.trackId !== undefined) {
        return state.stations.filter((station: any) =>
          station?.cohort?.id === query?.cohortId &&
          station?.administration?.id === query?.administrationId &&
          station?.track?.id === query?.trackId
        );
      } else {
        return state.stations;
      }
    } else {
      if (!query || query === undefined) {
        return state.stations;  // Return all stations when searchQuery is empty
      }
      return state.stations.filter((station: any) =>
        station?.cohort?.id?.includes((query as string).toLowerCase()) ||
        station?.administration?.id?.includes((query as string).toLowerCase()) ||
        station?.track?.id?.includes((query as string).toLowerCase()) ||
        station?.name?.toLowerCase().includes((query as string).toLowerCase())  // Include partial matches
      );
    }
  }

  @Action(Stations)
  getStations({ patchState }: StateContext<StationStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.stationService.getStations().pipe(
      tap((stations: IStationResponseModel[]) => {
        patchState({ stations: stations, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetActivityHistory)
  getActivityHistory(ctx: StateContext<StationStateModel>, action: GetActivityHistory) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.stationService.getActivityHistory(action.payload).pipe(
      tap((activityHistory: any[]) => {
        ctx.patchState({ activityHistory: activityHistory, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(Station)
  getStation(ctx: StateContext<StationStateModel>, action: Station) {
    return this.stationService.getStation(action.payload).pipe(
      tap((station: IStationResponseModel) => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          station: station,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error
      })
    );
  }

  @Action(CreateStation)
  createStation({ patchState, getState }: StateContext<StationStateModel>, action: CreateStation) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.stationService.createStation(action.payload).pipe(
      tap((result: StationRequestModel) => {
        const state = getState();
        patchState({
          stations: [...state.stations, result],
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateStation)
  updateStation({ patchState, getState }: StateContext<StationStateModel>, action: UpdateStation) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.stationService.updateStation(action.payload).pipe(
      tap((result) => {
        const stations = getState().stations.map((station) =>
          station.id === result.id ? result : station
        );
        patchState({
          stations,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error
      })
    );
  }

  @Action(DeleteStation)
  deleteStation({ patchState, getState }: StateContext<StationStateModel>, action: DeleteStation) {
    patchState({ loading: LoadingState.loadingDelete });
    return this.stationService.deleteStation(action.payload).pipe(
      tap((result: any) => {
        const state = getState();
        patchState({
          stations: state.stations.filter((station: StationRequestModel) => station.id !== action.payload),
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  // Action to set the search query
  @Action(SetStationSearch)
  setSearchQuery(ctx: StateContext<StationStateModel>, action: SetStationSearch) {
    // console.log('here:', action.query)
    ctx.patchState({ loading: LoadingState.loadingList });
    setTimeout(() => {
      ctx.patchState({
        searchQuery: action.query,
        loading: LoadingState.notLoading
      });
    }, 1000)
  }

  @Action(AssignStation)
  assignStation({ patchState, getState }: StateContext<StationStateModel>, action: AssignStation) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.stationService.assignStation(action.payload).pipe(
      tap((result: any) => {
        const state = getState();
        patchState({
          stations: [...state.stations, result],
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(Rotation)
  getRotation({ patchState, getState }: StateContext<StationStateModel>, action: Rotation) {
    patchState({ loading: LoadingState.loadingList });
    return this.stationService.getRotations(action.payload).pipe(
      tap((result: any[]) => {
        const state = getState();
        patchState({
          rotations: [...state.rotations, ...result],
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  // @Action(ReplaceScheduleInterviewer)
  // replaceScheduleInterviewer({ patchState }: StateContext<StationStateModel>, action: ReplaceScheduleInterviewer) {
  //   patchState({ loading: LoadingState.loadingAddUpdate });
  //   return this.stationService.replaceScheduleInterviewer(action.interviewerId, action.scheduleId).pipe(
  //     tap((result: any) => {
  //       patchState({ loading: LoadingState.notLoading });
  //     }),
  //     catchError((error) => {
  //       patchState({ loading: LoadingState.notLoading });
  //       throw error;
  //     })
  //   );
  // }

  // @Action(ReplaceStationInterviewer)
  // replaceStationInterviewer({ patchState }: StateContext<StationStateModel>, action: ReplaceStationInterviewer) {
  //   patchState({ loading: LoadingState.loadingAddUpdate });
  //   return this.stationService.replaceStationInterviewer(action.oldInterviewerId, action.newInterviewerId, action.stationId).pipe(
  //     tap((result: any) => {
  //       patchState({ loading: LoadingState.notLoading });
  //     }),
  //     catchError((error) => {
  //       patchState({ loading: LoadingState.notLoading });
  //       throw error;
  //     })
  //   );
  // }

}
